import { useRef, type PointerEvent, type MouseEvent, type ReactNode } from "react";

export function useDragScroll() {
  const ref = useRef<HTMLDivElement>(null);
  const isPointerDown = useRef(false);
  const hasMoved = useRef(false);
  const startX = useRef(0);
  const startScrollLeft = useRef(0);

  const onPointerDown = (e: PointerEvent<HTMLDivElement>) => {
    if (e.pointerType === "mouse" && e.button !== 0) return;
    isPointerDown.current = true;
    hasMoved.current = false;
    startX.current = e.clientX;
    startScrollLeft.current = ref.current?.scrollLeft ?? 0;
  };

  const onPointerMove = (e: PointerEvent<HTMLDivElement>) => {
    if (!isPointerDown.current || !ref.current) return;
    const deltaX = e.clientX - startX.current;
    if (Math.abs(deltaX) > 6) {
      hasMoved.current = true;
    }
    ref.current.scrollLeft = startScrollLeft.current - deltaX;
  };

  const onPointerUp = () => {
    isPointerDown.current = false;
  };

  const onClickCapture = (e: MouseEvent<HTMLDivElement>) => {
    if (!hasMoved.current) return;
    e.preventDefault();
    e.stopPropagation();
    hasMoved.current = false;
  };

  return {
    ref,
    onPointerDown,
    onPointerMove,
    onPointerUp,
    onPointerCancel: onPointerUp,
    onPointerLeave: onPointerUp,
    onClickCapture,
  };
}

interface DragScrollContainerProps {
  className?: string;
  children: ReactNode;
}

export function DragScrollContainer({ className, children }: DragScrollContainerProps) {
  const dragProps = useDragScroll();
  return (
    <div {...dragProps} className={className}>
      {children}
    </div>
  );
}
